import { Fragment } from 'react';
import { splitCodeSpans } from '../../utils/codeSpans';

/**
 * A rule's title as visible text (REV-1163). Engine titles use Markdown code
 * spans — "Ensure that referenced `id` attributes exist in the document." —
 * so each span renders as an inline <code> and its backticks are dropped. The
 * text content, and so the accessible name of whatever contains it, is the
 * title without backticks.
 *
 * Use this wherever a title is SHOWN. Where it is a plain string — an
 * aria-label, a chart label, a value sent to the API — use `stripCodeSpans`.
 */
export function RuleTitle({ text }: { text: string }) {
  return (
    <>
      {splitCodeSpans(text).map((tok, i) =>
        tok.type === 'code' ? (
          <code key={i}>{tok.value}</code>
        ) : (
          <Fragment key={i}>{tok.value}</Fragment>
        )
      )}
    </>
  );
}

/**
 * A translated string that has the rule title somewhere inside it, e.g.
 * "{{label}}, {{issues}}". Translate with `slot` as the title's value, and
 * this renders the result with the formatted title in the slot's place — so
 * the translation keeps control of word order.
 */
export function RuleTitleInTemplate({
  template,
  slot,
  title,
}: {
  template: string;
  slot: string;
  title: string;
}) {
  const parts = template.split(slot);
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <RuleTitle text={title} />}
          {part}
        </Fragment>
      ))}
    </>
  );
}
